import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';

const Counselling = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    employmentType: "",
    monthlyIncome: "",
    helpTopic: "",
    message: "",
    consultationDate: "",
    consultationTime: ""
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  // Financial guidance items
  const guidanceItems = [
    {
      icon: "📊",
      title: "CIBIL Score Improvement Guidance",
      description: "Understand your credit profile and how to improve it step by step"
    },
    {
      icon: "💳",
      title: "Debt Management Support",
      description: "Guidance on handling multiple obligations and reducing financial stress"
    },
    {
      icon: "📝",
      title: "Financial Planning Basics",
      description: "Simple strategies to manage your income and expenses better"
    },
    {
      icon: "📅",
      title: "Repayment Structuring Guidance",
      description: "Helping you plan repayments in a more manageable way"
    },
    {
      icon: "🧠",
      title: "Understanding Your Financial Behaviour",
      description: "Identify patterns and improve decision-making"
    },
    {
      icon: "🚫",
      title: "Avoiding Financial Mistakes",
      description: "Learn what to avoid to stay financially secure"
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      // Replace with your Google Apps Script Web App URL
      const scriptURL = 'https://script.google.com/macros/s/AKfycbw9iWCQUtGBXLWc5BRo6g--ZVlGceVx1upNq1AUuaGq8O04015WV7jwG023LdQ_NyTp/exec';
      
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      const response = await fetch(scriptURL, {
        method: 'POST',
        body: formDataToSend,
        mode: 'no-cors' // Important for Google Apps Script
      });

      setFormStatus({ submitting: false, submitted: true, error: null });
      
      // Show success sweet alert
      Swal.fire({
        title: '🎉 Consultation Booked!',
        text: 'Thank you for reaching out! Our financial counselor will contact you shortly to confirm your appointment.',
        icon: 'success',
        confirmButtonColor: '#2D5F7A',
        confirmButtonText: 'Great!',
        background: '#0F2128',
        color: '#fff',
        iconColor: '#7BC4E0',
        timer: 5000,
        timerProgressBar: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        }
      });
      
      // Reset form after successful submission
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        employmentType: "",
        monthlyIncome: "",
        helpTopic: "",
        message: "",
        consultationDate: "",
        consultationTime: ""
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, submitted: false }));
      }, 5000);

    } catch (error) {
      setFormStatus({ 
        submitting: false, 
        submitted: false, 
        error: "Something went wrong. Please try again." 
      });
      
      // Show error sweet alert
      Swal.fire({
        title: '❌ Submission Failed',
        text: 'There was an error submitting your request. Please try again or contact us directly.',
        icon: 'error',
        confirmButtonColor: '#2D5F7A',
        confirmButtonText: 'Try Again',
        background: '#0F2128',
        color: '#fff',
        iconColor: '#E07C7C'
      });
    }
  };

  // Function to show confirmation before submission
  const showConfirmation = (e) => {
    e.preventDefault();
    
    // Validate required fields before showing confirmation
    const requiredFields = ['fullName', 'email', 'phone', 'employmentType', 'monthlyIncome', 'helpTopic', 'consultationDate', 'consultationTime'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      Swal.fire({
        title: '⚠️ Missing Information',
        text: 'Please fill in all required fields before booking a consultation.',
        icon: 'warning',
        confirmButtonColor: '#2D5F7A',
        confirmButtonText: 'Got it',
        background: '#0F2128',
        color: '#fff',
        iconColor: '#FFB74D'
      });
      return;
    }
    
    // Show confirmation dialog
    Swal.fire({
      title: 'Confirm Your Consultation',
      html: `
        <div style="text-align: left; color: #CCE6F5;">
          <p style="margin-bottom: 10px;"><strong style="color: #7BC4E0;">Name:</strong> ${formData.fullName}</p>
          <p style="margin-bottom: 10px;"><strong style="color: #7BC4E0;">Email:</strong> ${formData.email}</p>
          <p style="margin-bottom: 10px;"><strong style="color: #7BC4E0;">Phone:</strong> ${formData.phone}</p>
          <p style="margin-bottom: 10px;"><strong style="color: #7BC4E0;">Date:</strong> ${formData.consultationDate}</p>
          <p style="margin-bottom: 10px;"><strong style="color: #7BC4E0;">Time:</strong> ${formData.consultationTime}</p>
          <p style="margin-bottom: 10px;"><strong style="color: #7BC4E0;">Topic:</strong> ${formData.helpTopic}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2D5F7A',
      cancelButtonColor: '#4A1E1E',
      confirmButtonText: 'Yes, Book it!',
      cancelButtonText: 'Cancel',
      background: '#0F2128',
      color: '#fff',
      iconColor: '#7BC4E0',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubmit(e);
      }
    });
  };

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* Dark gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#0A1620]"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>
        
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        <svg className="absolute top-0 left-0 w-full h-48 rotate-180" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>

        <svg className="absolute bottom-0 right-0 w-full h-48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      {/* Main Content */}
      <div className="relative z-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-28">
          
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
              </span>
              <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                FINANCIAL WELLNESS • GUIDANCE & SUPPORT
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Financial <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">Counselling</span>
            </h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-[#CCE6F5] font-light max-w-3xl mx-auto leading-relaxed"
            >
              At SalaryMates, we go beyond just assistance — we help you build better financial habits and stability.
            </motion.p>
          </motion.div>

          {/* Financial Support Section */}
          <div className="mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-3xl font-semibold text-white text-center mb-8"
            >
              🎯 What We Help You With
            </motion.h2>

            <motion.div
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1, delayChildren: 0.5 }
                }
              }}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {guidanceItems.map((item, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 }
                  }}
                  className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30 hover:border-[#7BC4E0]/50 transition-all duration-300"
                >
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-[#8FAFC7] text-sm">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Book Consultation Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[30px] p-8 md:p-10 border border-[#2D5F7A]/30"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Book a Free Consultation</h2>
              <p className="text-[#CCE6F5]">Speak with our financial counselor and get personalized guidance</p>
            </div>

            <form onSubmit={showConfirmation} className="grid md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white placeholder-[#8FAFC7]/50 focus:border-[#7BC4E0] focus:outline-none transition-colors"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white placeholder-[#8FAFC7]/50 focus:border-[#7BC4E0] focus:outline-none transition-colors"
                  placeholder="you@example.com"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white placeholder-[#8FAFC7]/50 focus:border-[#7BC4E0] focus:outline-none transition-colors"
                  placeholder="+91 98765 43210"
                />
              </div>

              {/* Employment Type */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Employment Type *</label>
                <select
                  name="employmentType"
                  value={formData.employmentType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white focus:border-[#7BC4E0] focus:outline-none transition-colors"
                >
                  <option value="">Select employment type</option>
                  <option value="salaried">Salaried</option>
                  <option value="self-employed">Self-Employed</option>
                  <option value="business">Business Owner</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Monthly Income */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Monthly Income Range *</label>
                <select
                  name="monthlyIncome"
                  value={formData.monthlyIncome}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white focus:border-[#7BC4E0] focus:outline-none transition-colors"
                >
                  <option value="">Select income range</option>
                  <option value="<25000">Less than ₹25,000</option>
                  <option value="25000-50000">₹25,000 - ₹50,000</option>
                  <option value="50000-100000">₹50,000 - ₹1,00,000</option>
                  <option value=">100000">More than ₹1,00,000</option>
                </select>
              </div>

              {/* Financial Goal */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">What would you like help with? *</label>
                <select
                  name="helpTopic"
                  value={formData.helpTopic}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white focus:border-[#7BC4E0] focus:outline-none transition-colors"
                >
                  <option value="">Select your goal</option>
                  <option value="cibil">CIBIL Score Improvement</option>
                  <option value="debt">Debt Management</option>
                  <option value="planning">Financial Planning</option>
                  <option value="repayment">Repayment Structuring</option>
                  <option value="behavior">Understanding Financial Behavior</option>
                  <option value="mistakes">Avoiding Financial Mistakes</option>
                </select>
              </div>

              {/* Consultation Date */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Preferred Date *</label>
                <input
                  type="date"
                  name="consultationDate"
                  value={formData.consultationDate}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white focus:border-[#7BC4E0] focus:outline-none transition-colors"
                />
              </div>

              {/* Consultation Time */}
              <div className="space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Preferred Time *</label>
                <select
                  name="consultationTime"
                  value={formData.consultationTime}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white focus:border-[#7BC4E0] focus:outline-none transition-colors"
                >
                  <option value="">Select time slot</option>
                  <option value="10am-12pm">10:00 AM - 12:00 PM</option>
                  <option value="12pm-2pm">12:00 PM - 2:00 PM</option>
                  <option value="2pm-4pm">2:00 PM - 4:00 PM</option>
                  <option value="4pm-6pm">4:00 PM - 6:00 PM</option>
                </select>
              </div>

              {/* Message - Full width */}
              <div className="md:col-span-2 space-y-2">
                <label className="text-[#B2D9F0] text-sm font-medium">Additional Message (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white placeholder-[#8FAFC7]/50 focus:border-[#7BC4E0] focus:outline-none transition-colors"
                  placeholder="Tell us more about your financial goals or concerns..."
                />
              </div>

              {/* Submit Button */}
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={formStatus.submitting}
                  className={`w-full py-4 bg-gradient-to-r from-[#2D5F7A] to-[#1E3B4A] text-white font-semibold rounded-lg border border-[#7BC4E0]/20 transition-all duration-300 ${
                    formStatus.submitting 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:scale-105 hover:shadow-lg hover:shadow-[#2D5F7A]/30'
                  }`}
                >
                  {formStatus.submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Book Free Consultation'
                  )}
                </button>
              </div>
            </form>

            <p className="text-center text-[#8FAFC7] text-sm mt-4">
              By submitting this form, you agree to be contacted by our financial counselor.
            </p>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
            className="flex flex-wrap justify-center items-center gap-6 mt-8"
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-[#8FAFC7] text-sm">Free Consultation</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-[#8FAFC7] text-sm">100% Confidential</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[#8FAFC7] text-sm">30-min Session</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative text */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        <span className="text-[#2D5F7A] text-sm">GUIDANCE</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#7BC4E0] text-sm">SUPPORT</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#2D5F7A] text-sm">STABILITY</span>
      </div>
    </section>
  );
};

export default Counselling;